import {
  EventType,
  PublicClientApplication,
  type SilentRequest,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import App from "innovate/App";
import { msalConfig } from "innovate/authConfig";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

// Chunk error recovery for post-deployment updates
// When a new version deploys, old cached HTML references chunks that no longer exist
// This automatically reloads the page once to fetch the latest version
const RELOAD_KEY = "vite-chunk-reload";

// Dynamic import failures from React.lazy/Vite are reported as Promise rejections,
// so listening to unhandledrejection is sufficient.
globalThis.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
  // Check reload flag INSIDE handler to avoid race conditions
  // (flag might be set between page load and error firing)
  if (sessionStorage.getItem(RELOAD_KEY)) {
    return; // Already reloaded once, don't reload again
  }

  // Safely extract error message from various error types
  // event.reason can be: Error object, string, or any thrown value
  let errorMessage = "";
  if (typeof event.reason === "string") {
    errorMessage = event.reason;
  } else if (event.reason instanceof Error) {
    errorMessage = event.reason.message || "";
  } else if (event.reason?.message) {
    errorMessage = String(event.reason.message);
  }

  // Detect Vite chunk loading errors (comprehensive pattern matching)

    const CHUNK_ERROR_PATTERNS = [
      "Failed to fetch dynamically imported module",
      "Importing a module script failed",
      "error loading dynamically imported module",
      "Failed to load module script",
    ];

    const isChunkError = CHUNK_ERROR_PATTERNS.some((pattern) =>
      errorMessage.includes(pattern)
    );

  if (isChunkError) {
    // Mark that we've reloaded (prevents infinite loops)
    sessionStorage.setItem(RELOAD_KEY, "true");
    
    // Reload to fetch latest version
    globalThis.location.reload();
  }
});

const msalInstance = new PublicClientApplication(msalConfig);
await msalInstance.initialize();

// Handle redirect promise BEFORE rendering app
// This processes the #code=...&state=... hash from Azure AD
// and navigates to redirectStartPage if provided
await msalInstance.handleRedirectPromise().catch((error) => {
  console.error("MSAL redirect handling failed:", error);
});

// Default to using the first account if no account is active on page load
if (
  !msalInstance.getActiveAccount() &&
  msalInstance.getAllAccounts().length > 0
) {
  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const acc = msalInstance.getAllAccounts()[0];
  if (acc) {
    msalInstance.setActiveAccount(acc);
  }
}

// Optional - This will update account state if a user signs in from another tab or window
msalInstance.enableAccountStorageEvents();

msalInstance.addEventCallback((event) => {
  const account = (event.payload as SilentRequest)?.account;
  if (event.eventType === EventType.LOGIN_SUCCESS && account) {
    msalInstance.setActiveAccount(account);
  }
});

const container = document.getElementById("root");
if (!container) throw new Error("Failed to find the root element");
const root = createRoot(container);
if (import.meta.env.DEV) {
  root.render(
    <StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </StrictMode>
  );
} else {
  root.render(
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  );
}

// eslint-disable-next-line no-void, @typescript-eslint/no-unsafe-call
